import { Link } from "@tanstack/react-router";
import { Download, Flame } from "lucide-react";
import { useMemo } from "react";
import { RiskBadge } from "@/components/skills/RiskBadge";
import type { SkillListItem } from "@/lib/skills/types";
import { cn } from "@/lib/utils";

/** Most-installed leaderboard: top 6 published skills by all-time downloads. */
export function TopSkills({ skills }: { skills: SkillListItem[] }) {
  const top = useMemo(
    () => [...skills].sort((a, b) => b.downloads - a.downloads).slice(0, 6),
    [skills],
  );
  if (top.length === 0) return null;

  return (
    <section>
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <h2 className="inline-flex items-center gap-2 font-display text-[19px] font-bold tracking-tight text-navy">
          <Flame className="size-[19px] text-cyan" />
          Top skills
        </h2>
        <span className="text-[12.5px] text-fg-3">Most installed across the team · all time</span>
      </div>
      <div className="grid grid-cols-2 gap-x-[22px] gap-y-2.5 rounded-[14px] border border-border-1 bg-white px-[18px] py-3 max-[1100px]:grid-cols-1">
        {top.map((s, i) => (
          <Link
            key={s.id}
            to="/skill/$id"
            params={{ id: s.id }}
            className="flex min-w-0 items-center gap-3.5 rounded-[10px] px-1.5 py-2.5 transition-colors hover:bg-bg-2"
          >
            <span
              className={cn(
                "w-7 shrink-0 text-center font-display text-[17px] font-bold tabular-nums",
                i < 3 ? "text-fg-link" : "text-[var(--border-strong)]",
              )}
            >
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate font-display text-[14.5px] font-bold text-navy">
                {s.title}
              </div>
              <div className="mt-0.5 line-clamp-2 text-xs leading-[1.45] text-fg-2">{s.desc}</div>
              <div className="mt-1 flex items-center gap-3 text-xs text-fg-3">
                <span className="inline-flex items-center gap-1.5 tabular-nums">
                  <Download className="size-[13px]" />
                  {s.downloads.toLocaleString()}
                </span>
                <span>★ {s.rating.toFixed(1)}</span>
                <RiskBadge level={s.risk} />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
